<template>
  <ontos-section :title="title" :icon="icon" :level="level" :dense="dense" :divider="divider"
                 :header="header">
    <template v-if="$slots.title" #title>
      <slot name="title"/>
    </template>
    <template v-if="$slots.subtitle" #subtitle>
      <slot name="subtitle"/>
    </template>
    <!-- Editável, o "Criar"; nos dois modos, o que quem hospeda puser (o "Editar" da leitura) -->
    <template #actions>
      <ontos-header-action v-if="editable" icon="mdi-plus" :label="createLabel" :disabled="addDisabled"
                           @click="add()"/>
      <slot name="actions"/>
    </template>

    <!-- O nome de cada coluna, nos dois modos (pedido do usuário: a leitura também), e só com alguma
         linha — sem nenhuma, os rótulos soltos não rotulariam nada. Editável, as marcas de
         obrigatório e único (a chave) ficam aqui, com o `.required`/`.unique` globais, e não em cada
         campo. -->
    <!-- Sem ele no telefone, na edição: lá o identificador vai embaixo do nome, e os nomes das colunas
         não teriam sobre o que cair. -->
    <div v-if="list.length && !stacked" class="entry-row entry-head" :class="{'entry-row--read': !editable}">
      <span v-if="editable" class="entry-color"/>
      <span class="entry-name" :class="{'required unique': editable}"><label>{{ $tt('name') }}</label></span>
      <span class="entry-alias" :class="{'required unique': editable}"><label>{{ $tt('alias') }}</label></span>
      <span v-for="column of columns" :key="column.key" class="entry-column" :style="widthOf(column)">
        <label>{{ column.label }}</label>
      </span>
    </div>

    <!-- Só leitura: o valor na forma da sua natureza — chip para papel e opção, bolinha (o
         semáforo) para situação — e o identificador -->
    <template v-if="!editable">
      <div v-for="(entry, index) of entries" :key="entry.alias || index" class="entry-row entry-row--read">
        <span class="entry-name">
          <ontos-colored-value :option="{id: index, name: entry.name, color: colors[index]}" :form="form"/>
        </span>
        <span class="entry-alias ontos-code text-medium-emphasis">{{ entry.alias }}</span>
        <span v-for="column of columns" :key="column.key" class="entry-column" :style="widthOf(column)">
          <slot :name="`cell-${column.key}`" :entry="entry" :index="index" :editable="false"/>
        </span>
      </div>
    </template>

    <!-- Editável: uma linha por entrada, sobre as linhas DESTE componente — quem grava é o Salvar
         de quem hospeda, que confere pelo `validateModel()`.

         O arrasto (`reorderable`) pela alça à direita; sem ele, o mesmo contêiner desligado, para a
         linha ser escrita uma vez só. E NADA de comentário dentro do `#item`: ele vira um nó, e o
         vuedraggable recusa a lista inteira com "Item slot must have only one child".

         O identificador é o que a fórmula cita: o da entrada gravada não muda. O da nova segue o nome
         no campo computado da casa, até alguém digitar nele. -->
    <draggable v-else v-model="rows" item-key="__key" handle=".ontos-grip" ghost-class="ontos-drop-ghost"
               :disabled="!reorderable" @end="changed('reorder')">
      <template #item="{element: row, index}">
        <div class="entry-row" :class="{'entry-row--stacked': stacked}">
          <span class="entry-color">
            <ontos-color-swatch-menu :model-value="row.color || ''" :fallback="colors[index]"
                                     :used="usedColors" :clearable="automaticColor" :title="$tt('color')"
                                     @update:model-value="setColor(row, $event)"/>
          </span>
          <span class="entry-ident">
            <v-text-field :model-value="row.name" :autofocus="row.__added" density="compact"
                          variant="underlined" hide-details="auto" class="entry-name"
                          :error-messages="shown(row, errors[index].name)" @blur="touched[row.__key] = true"
                          @update:model-value="change(row, {name: $event})"/>
            <span v-if="row.__stored" class="entry-alias ontos-code text-medium-emphasis"
                  :title="$tt('alias')">{{ row.alias }}</span>
            <ontos-computed-input v-else dense hide-details="auto" class="entry-alias ontos-code"
                                  :value="row.alias" :source="row.name"
                                  :manual="!!manual[row.__key]" @input:manual="manual[row.__key] = $event"
                                  :compute="$utils.aliasSuggestion" :compute-on-input="$utils.incompleteAliasSuggestion"
                                  :error-messages="shown(row, errors[index].alias)" @blur="touched[row.__key] = true"
                                  @input="change(row, {alias: $event})"/>
          </span>
          <span v-for="column of columns" :key="column.key" class="entry-column" :style="widthOf(column)">
            <slot :name="`cell-${column.key}`" :entry="row" :index="index" :editable="true"
                  :entries="rows" :set="setter(row)" :commit="emitEntries"/>
          </span>
          <span class="entry-actions">
            <v-btn variant="text" icon size="small" color="error" :title="$tt('delete')" @click="remove(row)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
            <v-icon v-if="reorderable" size="small" class="ontos-grip text-medium-emphasis">mdi-drag</v-icon>
          </span>
        </div>
      </template>
    </draggable>

    <div v-if="minError" class="text-body-2 text-error">{{ minError }}</div>
    <div v-else-if="!list.length && emptyText" class="text-body-2 text-grey">{{ emptyText }}</div>
  </ontos-section>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Section from '@/components/shared/Section.vue'
import HeaderAction from '@/components/shared/HeaderAction.vue'
import ComputedInput from '@/components/shared/ComputedInput.vue'
import ColorSwatchMenu from '@/components/shared/ColorSwatchMenu.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import {ValueForm} from '@/types/ColoredOption'
import {nextColor} from '@/utils/colors'
import {aliasSuggestion} from '@/utils/utils'
import draggable from 'vuedraggable'

/** A entrada que esta lista conhece: nome, alias e cor. O id e as marcas são de quem usa. */
export type ColoredEntry = { id?: number, alias: string, name: string, color?: string, [extra: string]: any }

/**
 * A entrada em edição: o que se grava, mais o que é só da linha — a chave estável (por onde o estado
 * dos campos dela é achado), se já estava gravada (o alias não muda) e se acabou de ser criada (o foco).
 */
export type EditableEntry = ColoredEntry & { __key: number, __stored: boolean, __added: boolean }

/** Uma coluna de quem usa, depois do identificador: o nome no cabeçalho e o slot `cell-<key>`. */
export type EntryColumn = { key: string, label: string, width?: string }

/** O que acabou de acontecer com a lista, para quem precisa corrigi-la antes de ela sair. */
export type EntryChange = { type: 'add' | 'remove' | 'reorder', entry?: EditableEntry }

let nextKey = 1

/**
 * Uma lista de entradas com nome, alias e cor — os papéis do ambiente, as opções de um domínio, as
 * situações de um fluxo. Não sabe qual das três é.
 *
 * Eram três editores para a mesma coisa (o `RolesSection`, o `ColoredOptionsInput` e o
 * `EntryListInput`), cada um com a sua validação e o seu leiaute; quando opção e situação ganharam
 * alias, as três listas ficaram iguais, e esta é a dos papéis (aprovada pelo usuário) generalizada.
 * O que cada uma tem de próprio entra por prop: a coluna das marcas Inicial/Final (`columns`), o
 * arrasto (`reorderable` — a ordem da opção e da situação é dado), a cor automática da situação
 * (`automaticColor`, `colorsOf`), o mínimo do fluxo (`min`) e a regra de uma situação inicial
 * (`normalize`).
 *
 * **Os dois modos.** Só leitura (a página de design, a da coleção, a do domínio e a do fluxo), mostra
 * `entries` como estão. Editável, lê `entries` UMA vez, no `created`, e devolve a lista inteira a
 * cada mudança (`update:entries`); quem hospeda confere pelo `validateModel()` antes de gravar — o
 * contrato que o domínio, o fluxo e o atributo de seleção já tinham com os editores de antes. Um
 * `@Watch` remapeando a cada eco do host descartaria as chaves, o estado dos campos e o foco: quem
 * precisa trocar a lista por fora remonta por `:key`.
 *
 * **O que já veio com alias está gravado**, e o alias dele não muda (o servidor também não deixa:
 * `withStoredAliases`). A semente do fluxo novo vem sem, e ganha o do nome ao entrar.
 */
@Component({
  name: 'ontos-colored-entry-list',
  emits: ['update:entries'],
  components: {
    'ontos-section': Section,
    'ontos-header-action': HeaderAction,
    'ontos-computed-input': ComputedInput,
    'ontos-color-swatch-menu': ColorSwatchMenu,
    'ontos-colored-value': ColoredValue,
    draggable
  }
})
class ColoredEntryList extends Vue {
  @Prop({type: Array, default: () => []}) readonly entries!: ColoredEntry[]
  @Prop({type: Boolean, default: false}) readonly editable!: boolean
  /** Como a entrada se desenha na leitura: `chip` (papel, opção) ou `dot` (situação, o semáforo). */
  @Prop({type: String, default: 'chip'}) readonly form!: ValueForm

  @Prop({type: String, default: ''}) readonly title!: string
  @Prop({type: String, default: ''}) readonly icon!: string
  @Prop({type: [String, Number], default: 2}) readonly level!: string | number
  @Prop({type: Boolean, default: false}) readonly dense!: boolean
  @Prop({type: Boolean, default: true}) readonly divider!: boolean
  /**
   * Repassado ao `Section`: sem cabeçalho quando quem hospeda já nomeia a lista — a aba "Papéis" do
   * design do ambiente, que repetiria o nome logo embaixo e tem as ações na linha das abas.
   */
  @Prop({type: Boolean, default: true}) readonly header!: boolean
  @Prop({type: String, default: ''}) readonly createLabel!: string
  @Prop({type: String, default: ''}) readonly emptyText!: string
  /** Trava o "Criar" enquanto não há a QUE acrescentar — o atributo sem domínio escolhido. */
  @Prop({type: Boolean, default: false}) readonly addDisabled!: boolean

  @Prop({type: Array, default: () => []}) readonly columns!: EntryColumn[]
  @Prop({type: Boolean, default: false}) readonly reorderable!: boolean
  /** Liga a volta para a cor automática, e faz a entrada nova nascer sem cor fixada. */
  @Prop({type: Boolean, default: false}) readonly automaticColor!: boolean
  /**
   * As cores em vigor, na ordem das linhas, quando nem toda entrada tem a sua. É função da LISTA, e
   * não da entrada, porque a cor automática é global: fixar uma tira aquela cor das outras.
   */
  @Prop({type: Function, default: undefined}) readonly colorsOf!: ((entries: ColoredEntry[]) => string[]) | undefined
  /** Quantas entradas a lista precisa ter para ser gravada — conferido no `validateModel`. */
  @Prop({type: Number, default: 0}) readonly min!: number
  /**
   * Corrige a lista DEPOIS de criar, remover ou reordenar, e ANTES de ela sair — mutando no lugar. É
   * por aqui que o fluxo garante uma situação inicial ao remover a que era; um evento sairia depois
   * da emissão, e uma lista sem começo chegaria a ser emitida.
   */
  @Prop({type: Function, default: undefined})
  readonly normalize!: ((entries: EditableEntry[], change: EntryChange) => void) | undefined
  /**
   * As linhas como estavam numa lista anterior (`rows`), no lugar das `entries` — o Expandir da janela de
   * propriedade, com as opções mexidas e não gravadas. Pelas linhas, e não pelas entradas: refeitas das
   * entradas, a opção nova pareceria gravada (é o alias que diz `__stored`), e o identificador dela travaria.
   */
  @Prop({type: Array, default: null}) readonly resumed!: EditableEntry[] | null

  rows: EditableEntry[] = []
  /** Tentou-se salvar: todo erro aparece, inclusive o obrigatório de linha em que ninguém tocou. */
  revealed = false
  /**
   * O estado dos CAMPOS, pela chave da linha, e não na entrada: quais têm o identificador digitado (o
   * "manual" do campo computado) e de quais já se saiu de um campo (o que libera o obrigatório).
   *
   * Na entrada, ele ia e voltava pelo host, e o campo computado emite duas coisas no mesmo instante —
   * "virou manual" e o valor. A segunda partia da prop velha e apagava a primeira: o botão de voltar
   * ao automático sumia (relato do usuário, nos papéis).
   */
  manual: Record<number, boolean> = {}
  touched: Record<number, boolean> = {}

  created() {
    if (this.editable) this.rows = this.resumed || this.entries.map(entry => this.asEditable(entry, false))
  }

  private asEditable(entry: ColoredEntry, added: boolean): EditableEntry {
    const stored = !!entry.alias
    return {...entry, alias: stored ? entry.alias : aliasSuggestion(entry.name || ''),
      __key: nextKey++, __stored: stored, __added: added}
  }

  get list(): ColoredEntry[] {
    return this.editable ? this.rows : this.entries
  }

  /** A cor em vigor de cada linha: a escolhida, ou a que `colorsOf` resolve. */
  get colors(): string[] {
    return this.colorsOf ? this.colorsOf(this.list) : this.list.map(entry => entry.color || '')
  }

  /** As cores ESCOLHIDAS, para a paleta marcar as ocupadas e a nova nascer livre. */
  get usedColors(): string[] {
    return this.rows.map(row => row.color).filter((color): color is string => !!color)
  }

  /**
   * Na edição, no telefone, o identificador vai embaixo do nome, pequeno, e a linha continua uma só
   * (escolha do usuário). Lado a lado, com a cor, as marcas, a lixeira e a alça, o nome e o
   * identificador ficavam com uma letra cada.
   */
  get stacked(): boolean {
    return this.editable && this.$vuetify.display.xs
  }

  /**
   * Na leitura a coluna encolhe junto com as outras; na edição, não, que ela guarda campos (as caixas
   * Inicial e Final do fluxo). Fixa também na leitura, ela ficava com a largura toda no telefone, e o
   * nome e o identificador se espremiam até quebrar no meio da palavra (relato do usuário).
   */
  widthOf(column: EntryColumn) {
    return column.width ? {flex: `0 ${this.editable ? 0 : 1} ${column.width}`} : {}
  }

  /**
   * Nome e identificador obrigatórios e únicos na lista, sem distinguir maiúsculas — a régua do
   * servidor (`NamedList.asValidList`).
   */
  get errors(): Array<{ name: string, alias: string }> {
    const valueOf = (row: EditableEntry, field: 'name' | 'alias') => (row[field] || '').trim().toLowerCase()
    const error = (row: EditableEntry, field: 'name' | 'alias') =>
        !valueOf(row, field) ? this.$tt('required-message') as string
            : this.rows.some(other => other !== row && valueOf(other, field) === valueOf(row, field))
                ? this.$tt('validation.unique-field') as string : ''
    return this.rows.map(row => ({name: error(row, 'name'), alias: error(row, 'alias')}))
  }

  get minError(): string {
    return this.editable && this.revealed && this.rows.length < this.min
        ? this.$tt('validation.min-entries', {min: this.min}) as string : ''
  }

  /**
   * O erro de um campo como a linha o mostra: o "obrigatório" só depois de sair de um campo dela, ou
   * de tentar salvar — o padrão da casa. Acusar na linha que acabou de nascer, antes de alguém
   * escrever, era abri-la em vermelho (relato do usuário). O "já usado" vale na hora: só acontece com
   * algo escrito.
   */
  shown(row: EditableEntry, error: string): string {
    if (error !== this.$tt('required-message')) return error
    return this.touched[row.__key] || this.revealed ? error : ''
  }

  /**
   * A entrada sem o que é só da linha, chave a chave: o que esta lista não conhece (o `initial` de
   * uma situação) passa intacto, e a chave ausente continua ausente — voltando como `undefined`
   * explícito, o diff de quem hospeda enxergaria mudança onde não houve.
   */
  private persisted(row: EditableEntry): ColoredEntry {
    const persisted: any = {}
    Object.keys(row).filter(key => !key.startsWith('__')).forEach(key => persisted[key] = row[key])
    return {...persisted, name: row.name.trim(), alias: row.alias.trim()}
  }

  /** A única saída: sempre objetos novos. Também o `commit` do slot que mexeu na lista por conta própria. */
  emitEntries() {
    this.$emit('update:entries', this.rows.map(row => this.persisted(row)))
  }

  /** Mudou a estrutura: corrige primeiro, emite depois. O `@end` do arrasto chega aqui. */
  changed(type: EntryChange['type'], entry?: EditableEntry) {
    if (this.normalize) this.normalize(this.rows, {type, entry})
    this.emitEntries()
  }

  /** Para o slot: mexer num campo da própria linha e emitir. */
  setter(row: EditableEntry) {
    return (field: string, value: any) => this.change(row, {[field]: value})
  }

  change(row: EditableEntry, changes: Partial<ColoredEntry>) {
    Object.assign(row, changes)
    this.emitEntries()
  }

  /**
   * Cor já escolhida por outra linha TROCA com ela, em vez de recusar ou sortear outra: o conjunto
   * em uso continua o mesmo, o que é previsível para quem monta a lista. Só com quem tem cor FIXADA:
   * uma linha de cor automática não ganha cor escolhida por reflexo de outra.
   */
  setColor(row: EditableEntry, color: string) {
    const other = this.rows.find(candidate => candidate !== row && candidate.color && candidate.color === color)
    if (other) this.paint(other, row.color)
    this.paint(row, color)
    this.emitEntries()
  }

  /** Sem cor é a chave AUSENTE (a automática), e não `undefined` — ver o `persisted`. */
  private paint(row: EditableEntry, color: string | undefined) {
    if (color) row.color = color
    else delete row.color
  }

  add() {
    const color = this.automaticColor ? {} : {color: nextColor(this.usedColors)}
    this.rows.push(this.asEditable({name: '', alias: '', ...color}, true))
    this.changed('add')
  }

  /**
   * Remove sem travar no mínimo, mesmo esvaziando a lista: travar o último botão escondia a regra num
   * controle desligado, e impedia o caminho natural de apagar tudo e recomeçar. O mínimo é exigido ao
   * gravar.
   */
  remove(row: EditableEntry) {
    this.rows = this.rows.filter(candidate => candidate !== row)
    this.changed('remove', row)
  }

  /** O contrato com quem hospeda, antes de gravar: todos os erros aparecem de uma vez. */
  validateModel(): boolean {
    this.revealed = true
    return this.rows.length >= this.min && this.errors.every(({name, alias}) => !name && !alias)
  }
}

export default toNative(ColoredEntryList)
</script>

<style scoped>
/* As colunas nas mesmas larguras de uma linha para a outra, e no cabeçalho: os nomes delas caem
   sobre os campos */
.entry-row {
  align-items: center;
  display: flex;
  gap: 12px;
}

.entry-name {
  flex: 0 1 280px;
  min-width: 0;
}

.entry-alias {
  flex: 0 1 220px;
  font-size: 0.8125rem;
  min-width: 0;
}

.entry-color {
  flex: 0 0 32px;
}

/* Fora do telefone a caixa não existe para o layout: o nome e o identificador são colunas da linha. */
.entry-ident {
  display: contents;
}

/* No telefone, uma célula só, que fica com a largura que sobra: o nome em cima e o identificador
   embaixo, menor — editável ali mesmo, ao tocar. O vão aperta para sobrar largura ao nome. */
.entry-row--stacked {
  gap: 8px;
}

.entry-row--stacked .entry-ident {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.entry-row--stacked .entry-name,
.entry-row--stacked .entry-alias {
  flex: none;
}

.entry-row--stacked .entry-alias {
  font-size: 0.75rem;
}

.entry-row--stacked .entry-alias :deep(input) {
  font-size: 0.75rem;
}

.entry-column {
  min-width: 0;
}

/* Na leitura, cada célula numa linha só, com reticências quando não cabe — no cabeçalho também.
   Quebrando, o telefone desalinhava a tabela: a bolinha numa linha e o nome partido na outra
   ("Pendent / e", pelo `overflow-wrap: anywhere` do valor), e o rótulo "Identificador" invadindo a
   coluna vizinha (relato do usuário). */
.entry-row--read > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entry-row--read :deep(.option-dot-line) {
  overflow-wrap: normal;
}

.entry-actions {
  align-items: center;
  display: flex;
}

/* Os nomes das colunas nas medidas dos cabeçalhos de tabela da casa */
.entry-head {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
